import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { Product } from "@/types/product";
import { formatPrice } from "@/data/products";
import { ProductImagePlaceholder } from "@/components/products/ProductImagePlaceholder";
import { cloudinaryProductImageUrl, hasProductImage } from "@/lib/productImages";

interface ProductCardProps {
  product: Product;
  index: number;
}

export const ProductCard = ({ product, index }: ProductCardProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = cloudinaryProductImageUrl(product.image, "card");
  const showImage = hasProductImage(imageUrl) && !imageFailed;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="group border border-black/10 bg-white flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/products/${product.id}`} className="flex flex-col h-full">
        <div className="img-frame aspect-square relative bg-[#f9fafb]">
          {showImage ? (
            <img
              src={imageUrl}
              alt={product.name}
              width={400}
              height={400}
              loading={index < 4 ? "eager" : "lazy"}
              className="w-full h-full object-cover absolute inset-0"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <ProductImagePlaceholder iconSize={44} />
          )}
          {product.unit ? (
            <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-white/90 backdrop-blur px-2 py-1 sm:px-3 sm:py-1.5 font-mono-acc text-[10px] sm:text-xs uppercase tracking-[0.2em]">
              {product.unit}
            </div>
          ) : null}
        </div>
        <div className="p-2.5 sm:p-4 flex flex-col gap-1.5 sm:gap-2 border-t border-black/10 flex-1">
          <div className="min-w-0">
            <p className="font-mono-acc text-[9px] sm:text-[10px] uppercase tracking-[0.15em] text-vg-gray truncate">{product.brand}</p>
            <h3 className="font-heading font-bold text-xs sm:text-base tracking-tight mt-0.5 leading-snug line-clamp-2">{product.name}</h3>
          </div>
          <div className="flex items-center justify-between pt-1.5 sm:pt-2 border-t border-black/5 mt-auto gap-2">
            <span className="font-heading font-black text-base sm:text-xl tracking-tighter truncate">{formatPrice(product.price)}</span>
            <span className="font-mono-acc text-[9px] uppercase tracking-[0.15em] text-vg-gray group-hover:text-vg-cyan transition-colors">
              View
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

export default ProductCard;
